import type { Components } from "react-markdown";
import MarkdownRenderer from "@/components/content/MarkdownRenderer";
import { cn } from "@/utils/cn";

interface ChatMessageContentProps {
  content: string;
  className?: string;
}

export default function ChatMessageContent({
  content,
  className,
}: ChatMessageContentProps) {
  const components: Components = {
    h1: ({ children }) => (
      <h1 className="mb-3 mt-4 text-lg font-semibold first:mt-0">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="mb-2 mt-4 text-base font-semibold first:mt-0">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="mb-2 mt-3 text-sm font-semibold first:mt-0">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="mb-1 mt-3 text-sm font-medium first:mt-0">
        {children}
      </h4>
    ),
    p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
    strong: ({ children }) => (
      <strong className="font-semibold">{children}</strong>
    ),
    code: ({ children }) => (
      <code
        className={cn("rounded px-1.5 py-0.5 font-mono text-xs", "bg-muted text-foreground")}
      >
        {children}
      </code>
    ),
    pre: ({ children }) => (
      <pre
        className={cn("my-2 overflow-x-auto rounded p-2 font-mono text-xs", "bg-muted text-foreground")}
      >
        {children}
      </pre>
    ),
    ul: ({ children }) => (
      <ul className="mb-2 list-inside list-disc space-y-1 last:mb-0">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="mb-2 list-inside list-decimal space-y-1 last:mb-0">
        {children}
      </ol>
    ),
    blockquote: ({ children }) => (
      <blockquote
        className={cn("my-2 border-l-2 pl-3 italic", "border-border text-muted-foreground")}
      >
        {children}
      </blockquote>
    ),
    hr: () => (
      <hr
        className={cn("my-3 border-0 border-t", "border-border")}
      />
    ),
    table: ({ children }) => (
      <div className="my-2 overflow-x-auto">
        <table className="w-full border-collapse text-left text-xs">
          {children}
        </table>
      </div>
    ),
    th: ({ children }) => (
      <th className="border border-current/20 px-2 py-1 font-semibold">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border border-current/20 px-2 py-1">{children}</td>
    ),
    a: ({ href, children }) => (
      <a
        href={href}
        className={cn("text-link underline underline-offset-[3px] hover:text-link-hover")}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
  };

  return (
    <div className={cn("break-words", className)}>
      <MarkdownRenderer
        content={content}
        fallback={<div className="whitespace-pre-wrap">{content}</div>}
        components={components}
      />
    </div>
  );
}
